import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import cls from "./SingleUser.module.scss";
import api from "@/utils/axios";
import { timeAgo } from "@/utils/timeAgo";

const SingleUser = () => {
  const navigate = useNavigate();
  const { userId } = useParams();

  const [user, setUser] = useState(null);
  const [memberInfo, setMemberInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get(`/v1/admin/members/single/${userId}`);
      // controller returns the user plus memberInfo only when a Member model exists.
      setUser(res.data.user || null);
      setMemberInfo(res.data.memberInfo || null);
    } catch (err) {
      console.error("Error loading user", err);
      setError("Failed to load user details.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchUser();
    }
  }, [fetchUser, userId]);

  const handleSuspend = async () => {
    if (!user) return;
    try {
      setActionLoading(true);
      await api.patch(`/v1/admin/members/${user._id}/suspend`);
      setUser((prev) => (prev ? { ...prev, status: "suspended" } : prev));
    } catch (err) {
      console.error("Error suspending user:", err);
      setError("Failed to suspend user.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBan = async () => {
    if (!user) return;
    try {
      setActionLoading(true);
      await api.patch(`/v1/admin/members/${user._id}/ban`);
      setUser((prev) => (prev ? { ...prev, status: "banned" } : prev));
    } catch (err) {
      console.error("Error banning user:", err);
      setError("Failed to ban user.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;

    const confirmed = window.confirm(
      `Delete @${user.username}? This will mark the account as deleted.`,
    );

    if (!confirmed) return;

    try {
      setActionLoading(true);
      await api.patch(`/v1/admin/members/${user._id}/delete`);
      setUser((prev) => (prev ? { ...prev, status: "deleted" } : prev));
    } catch (err) {
      console.error("Error deleting user:", err);
      setError("Failed to delete user.");
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  };

  const formatLabel = (v) => {
    if (!v) return "";
    return v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const statusClass = useMemo(() => {
    if (!user?.status) return "";
    if (user.status === "active") return cls.statusActive;
    if (user.status === "suspended") return cls.statusSuspended;
    if (user.status === "banned") return cls.statusBanned;
    if (user.status === "deleted") return cls.statusDeleted;
    return "";
  }, [user]);

  if (loading) {
    return (
      <div className={cls.page}>
        <div className={cls.headerRow}>
          <button
            type="button"
            className={cls.backBtn}
            onClick={() => navigate("/admin/members")}
          >
            ← Back to Members
          </button>
        </div>
        <div className={cls.loadingCard}>
          <span className={cls.spinner} />
          <span>Loading user details...</span>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className={cls.page}>
        <div className={cls.headerRow}>
          <button
            type="button"
            className={cls.backBtn}
            onClick={() => navigate("/admin/members")}
          >
            ← Back to Members
          </button>
        </div>
        <div className={cls.errorCard}>
          <p>{error || "User not found."}</p>
        </div>
      </div>
    );
  }

  const hasMemberProfile = Boolean(memberInfo);
  const avatarLetter =
    (user.username && user.username.charAt(0).toUpperCase()) || "?";
  const hasMemberAvatar = !!memberInfo?.avatar?.url;

  const memberTypeLabel = memberInfo?.memberType
    ? formatLabel(memberInfo.memberType)
    : null;
  const memberGenderLabel = memberInfo?.gender
    ? formatLabel(memberInfo.gender)
    : null;

  return (
    <div className={cls.page}>
      <div className={cls.headerRow}>
        <button
          type="button"
          className={cls.backBtn}
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>

        <div className={cls.titleBlock}>
          <h1 className={cls.title}>User Profile</h1>
          <p className={cls.subtitle}>
            Manage account status and inspect all stored user details. For
            members, you also see their Noty membership snapshot.
          </p>
        </div>
      </div>

      <div className={cls.summaryRow}>
        <div className={cls.userChip}>
          <span
            className={
              hasMemberAvatar
                ? `${cls.userAvatar} ${cls.userAvatarHasImage}`
                : cls.userAvatar
            }
            style={
              hasMemberAvatar
                ? { backgroundImage: `url(${memberInfo.avatar.url})` }
                : undefined
            }
          >
            {!hasMemberAvatar && avatarLetter}
          </span>
          <div className={cls.userChipText}>
            <div className={cls.userNameLine}>@{user.username}</div>
            <div className={cls.userEmailLine}>{user.email}</div>
          </div>
        </div>

        <div className={cls.metaChips}>
          <span className={`${cls.statusChip} ${statusClass}`}>
            Status: {user.status}
          </span>
          <span className={cls.roleChip}>Role: {user.role}</span>
          {user.age != null && (
            <span className={cls.ageChip}>Age: {user.age}</span>
          )}

          {/* Member-only quick chips */}
          {hasMemberProfile && (
            <>
              {memberTypeLabel && (
                <span className={cls.memberTypeChip}>
                  Member Type: {memberTypeLabel}
                </span>
              )}
            </>
          )}
        </div>
        {hasMemberProfile && (
          <button
            type="button"
            className={cls.backBtn}
            onClick={() =>
              navigate(`/admin/members/single/profile-preview/${user._id}`)
            }
          >
            Profile Preview
          </button>
        )}
      </div>

      {/* Member membership strip */}
      {hasMemberProfile ? (
        <section className={cls.memberStrip}>
          <div className={cls.memberStripLeft}>
            <div className={cls.memberStripLabel}>Noty Membership</div>
            <div className={cls.memberStripBadges}>
              {memberTypeLabel && (
                <span className={cls.memberStripBadgePrimary}>
                  {memberTypeLabel === "Couple"
                    ? "Couple Membership"
                    : "Single Membership"}
                </span>
              )}
              {memberGenderLabel && (
                <span className={cls.memberStripBadgeSoft}>
                  Primary gender: {memberGenderLabel}
                </span>
              )}
            </div>
          </div>
          <div className={cls.memberStripActions}>
            {memberInfo.avatar?.key && (
              <div className={cls.memberStripRight}>
                <span className={cls.memberAvatarKeyLabel}>Avatar Key</span>
                <span className={cls.memberAvatarKeyValue}>
                  {memberInfo.avatar.key}
                </span>
              </div>
            )}
            <button
              type="button"
              className={cls.memberProfileBtn}
              onClick={() => navigate(`/admin/members/single/${user._id}`)}
            >
              Connect Member Profile
            </button>
          </div>
        </section>
      ) : (
        <section className={cls.noMemberStrip}>
          <div className={cls.memberStripLabel}>Member Profile</div>
          <p>No member profile</p>
        </section>
      )}

      <div className={cls.actionsRow}>
        <button
          type="button"
          className={`${cls.actionBtn} ${cls.editBtn}`}
          disabled={actionLoading}
          onClick={() =>
            navigate(`/admin/members/single/login-info/${user._id}`)
          }
        >
          Update User Info
        </button>
        <button
          type="button"
          className={`${cls.actionBtn} ${cls.suspendBtn}`}
          disabled={actionLoading || user.status === "suspended"}
          onClick={handleSuspend}
        >
          {user.status === "suspended" ? "Already Suspended" : "Suspend User"}
        </button>
        <button
          type="button"
          className={`${cls.actionBtn} ${cls.banBtn}`}
          disabled={actionLoading || user.status === "banned"}
          onClick={handleBan}
        >
          {user.status === "banned" ? "Already Banned" : "Ban User"}
        </button>
        <button
          type="button"
          className={`${cls.actionBtn} ${cls.deleteBtn}`}
          disabled={actionLoading || user.status === "deleted"}
          onClick={handleDelete}
        >
          {user.status === "deleted" ? "Already Deleted" : "Delete User"}
        </button>
      </div>

      <div className={cls.grid}>
        {/* Account basics */}
        <section className={cls.card}>
          <h2 className={cls.cardTitle}>Account Basics</h2>
          <div className={cls.cardBody}>
            <div className={cls.fieldRow}>
              <span className={cls.fieldLabel}>Username</span>
              <span className={cls.fieldValue}>@{user.username}</span>
            </div>
            <div className={cls.fieldRow}>
              <span className={cls.fieldLabel}>Email</span>
              <span className={cls.fieldValue}>{user.email}</span>
            </div>
            <div className={cls.fieldRow}>
              <span className={cls.fieldLabel}>Role</span>
              <span className={cls.fieldValue}>{user.role}</span>
            </div>
            <div className={cls.fieldRow}>
              <span className={cls.fieldLabel}>Status</span>
              <span className={`${cls.fieldValue} ${statusClass}`}>
                {user.status}
              </span>
            </div>
            <div className={cls.fieldRow}>
              <span className={cls.fieldLabel}>Created</span>
              <span className={cls.fieldValue}>
                {formatDate(user.createdAt)}{" "}
                <span className={cls.muted}>({timeAgo(user.createdAt)})</span>
              </span>
            </div>
            <div className={cls.fieldRow}>
              <span className={cls.fieldLabel}>Last Updated</span>
              <span className={cls.fieldValue}>
                {formatDate(user.updatedAt)}
              </span>
            </div>
          </div>
        </section>

        {/* Identity & birthday */}
        <section className={cls.card}>
          <h2 className={cls.cardTitle}>Identity & Age</h2>
          <div className={cls.cardBody}>
            <div className={cls.fieldRow}>
              <span className={cls.fieldLabel}>Birthday</span>
              <span className={cls.fieldValue}>
                {user.birthday ? formatDate(user.birthday) : "—"}
              </span>
            </div>
            <div className={cls.fieldRow}>
              <span className={cls.fieldLabel}>Age (virtual)</span>
              <span className={cls.fieldValue}>
                {user.age != null ? user.age : "—"}
              </span>
            </div>
          </div>
        </section>

        {/* Verification & Security */}
        <section className={cls.card}>
          <h2 className={cls.cardTitle}>Verification & Security</h2>
          <div className={cls.cardBody}>
            <div className={cls.fieldRow}>
              <span className={cls.fieldLabel}>Email Verified At</span>
              <span className={cls.fieldValue}>
                {user.emailVerifiedAt ? formatDate(user.emailVerifiedAt) : "—"}
              </span>
            </div>
            <div className={cls.fieldRow}>
              <span className={cls.fieldLabel}>Two Factor</span>
              <span className={cls.fieldValue}>
                {user.twoFactorEnabled ? "Enabled" : "Disabled"}
              </span>
            </div>
            <div className={cls.fieldRow}>
              <span className={cls.fieldLabel}>Last Login</span>
              <span className={cls.fieldValue}>
                {user.lastLoginAt ? formatDate(user.lastLoginAt) : "—"}
              </span>
            </div>
            <div className={cls.fieldRow}>
              <span className={cls.fieldLabel}>Failed Logins</span>
              <span className={cls.fieldValue}>
                {user.failedLoginCount ?? 0}
              </span>
            </div>
            <div className={cls.fieldRow}>
              <span className={cls.fieldLabel}>Last Password Change</span>
              <span className={cls.fieldValue}>
                {user.lastPasswordChangeAt
                  ? formatDate(user.lastPasswordChangeAt)
                  : "—"}
              </span>
            </div>
          </div>
        </section>

        {/* Legal & System */}
        <section className={cls.card}>
          <h2 className={cls.cardTitle}>Legal & System Flags</h2>
          <div className={cls.cardBody}>
            <div className={cls.fieldRow}>
              <span className={cls.fieldLabel}>TOS Accepted</span>
              <span className={cls.fieldValue}>
                {user.tosAcceptedAt ? formatDate(user.tosAcceptedAt) : "—"}
              </span>
            </div>
            <div className={cls.fieldRow}>
              <span className={cls.fieldLabel}>Privacy Accepted</span>
              <span className={cls.fieldValue}>
                {user.privacyAcceptedAt
                  ? formatDate(user.privacyAcceptedAt)
                  : "—"}
              </span>
            </div>
            <div className={cls.fieldRow}>
              <span className={cls.fieldLabel}>Root User</span>
              <span className={cls.fieldValue}>
                {user.isRoot ? "Yes" : "No / Hidden"}
              </span>
            </div>
          </div>
        </section>

        {/* {extraFields.length > 0 && (
          <section className={cls.card}>
            <h2 className={cls.cardTitle}>Additional User Fields</h2>
            <div className={cls.cardBody}>
              {extraFields.map((field) => (
                <div className={cls.fieldRow} key={field.key}>
                  <span className={cls.fieldLabel}>
                    {formatLabel(field.key)}
                  </span>
                  <span className={cls.fieldValue}>{field.value || "—"}</span>
                </div>
              ))}
            </div>
          </section>
        )} */}
      </div>

      {/* Linked detail pages */}
      <section className={cls.linksSection}>
        <h2 className={cls.linksTitle}>Deep Dive</h2>
        <div className={cls.linkGrid}>
          {/* Login info card - always visible */}
          <button
            type="button"
            className={cls.linkCard}
            onClick={() =>
              navigate(`/admin/members/single/login-info/${user._id}`)
            }
          >
            <div className={cls.linkCardTitle}>Login Info</div>
            <p className={cls.linkCardText}>
              View and Edit username, email, and password
            </p>
          </button>

          {/* Member-only cards */}
          {hasMemberProfile && (
            <>
              <button
                type="button"
                className={`${cls.linkCard} ${cls.cardMember}`}
                onClick={() =>
                  navigate(`/admin/members/single/member-info/${user._id}`)
                }
              >
                <div className={cls.linkCardTitle}>Member Info</div>
                <p className={cls.linkCardText}>See membership details</p>
              </button>
              <button
                type="button"
                className={`${cls.linkCard} ${cls.cardPersonal}`}
                onClick={() =>
                  navigate(
                    `/admin/members/single/member-personal-info/${user._id}`,
                  )
                }
              >
                <div className={cls.linkCardTitle}>Member Personal Info</div>
                <p className={cls.linkCardText}>
                  See private personal details collected during onboarding.
                </p>
              </button>

              <button
                type="button"
                className={`${cls.linkCard} ${cls.cardLifestyle}`}
                onClick={() =>
                  navigate(
                    `/admin/members/single/member-lifestyle-info/${user._id}`,
                  )
                }
              >
                <div className={cls.linkCardTitle}>Lifestyle Info</div>
                <p className={cls.linkCardText}>
                  Review their lifestyle selections, interests and boundaries.
                </p>
              </button>

              <button
                type="button"
                className={`${cls.linkCard} ${cls.cardPartners}`}
                onClick={() =>
                  navigate(`/admin/members/single/partners-info/${user._id}`)
                }
              >
                <div className={cls.linkCardTitle}>Partners Info</div>
                <p className={cls.linkCardText}>
                  Explore primary and secondary partner details for this member.
                </p>
              </button>

              <button
                type="button"
                className={`${cls.linkCard} ${cls.cardPayment}`}
                onClick={() =>
                  navigate(`/admin/members/single/payment-info/${user._id}`)
                }
              >
                <div className={cls.linkCardTitle}>Payment Info</div>
                <p className={cls.linkCardText}>
                  Explore payment details for this member.
                </p>
              </button>

              <button
                type="button"
                className={`${cls.linkCard} ${cls.cardPayment}`}
                onClick={() =>
                  navigate(`/admin/members/single/posts/${user._id}`)
                }
              >
                <div className={cls.linkCardTitle}>Posts</div>
                <p className={cls.linkCardText}>
                  Review all posts this member created and inspect each post
                  details.
                </p>
              </button>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default SingleUser;
